import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type RecordApprovalsResult } from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';
import { ApprovalChainView } from './approval-chain.js';

type ObjectKey = 'deal' | 'quote' | 'account' | 'contact' | 'lead';
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

/**
 * The approval banner on a record (F-090): submit it to a process, see the
 * whole chain and who it waits on, decide if it waits on you, recall it if you
 * asked.
 */
export function RecordApprovalsPanel({ objectKey, recordId }: { objectKey: ObjectKey; recordId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-090');
  const { session } = useSession();
  const [processId, setProcessId] = useState('');
  const [comment, setComment] = useState('');

  const approvals = useQuery<RecordApprovalsResult, ApiError>({
    queryKey: ['record-approvals', objectKey, recordId],
    queryFn: () => api.recordApprovals({ path: { objectKey, recordId } }),
    enabled: enabled.enabled,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['record-approvals', objectKey, recordId] });
  const submit = useMutation({
    mutationFn: () => api.submitForApproval({ path: { objectKey, recordId }, body: { processId, comment: comment.trim() || null } }),
    onSuccess: async () => {
      setComment('');
      await refresh();
    },
  });
  const decide = useMutation({
    mutationFn: (input: { requestId: string; decision: 'approved' | 'rejected' }) =>
      api.decideApprovalStep({ path: { requestId: input.requestId }, body: { decision: input.decision, comment: comment.trim() || null } }),
    onSuccess: async () => {
      setComment('');
      await refresh();
    },
  });
  const recall = useMutation({
    mutationFn: (requestId: string) => api.recallApproval({ path: { requestId }, body: { comment: null } }),
    onSuccess: refresh,
  });

  if (!enabled.enabled) return null;
  if (approvals.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (approvals.isError) return <p className="text-dense text-danger-text">{errorText(approvals.error)}</p>;
  const { requests, processes } = approvals.data;
  const pending = requests.find((request) => request.status === 'pending') ?? null;
  const me = session.user.id;
  const waitingOnMe =
    pending?.steps.some((step) => step.state === 'current' && step.assignees.some((a) => a.userId === me && a.status === 'pending')) ?? false;
  const refusal = submit.error ?? decide.error ?? recall.error;
  if (requests.length === 0 && processes.length === 0) return null;

  return (
    <Panel data-region="record-approvals">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('approvals.title')}</SectionLabel>
        {refusal ? <p data-approval-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
        {pending ? (
          <div data-approval-banner className="flex flex-col gap-2 rounded-card bg-warning-tint p-3">
            <ApprovalChainView chain={pending} />
            {waitingOnMe ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  data-approval-comment
                  aria-label={t('approvals.comment')}
                  placeholder={t('approvals.comment')}
                  className={`${INPUT} min-w-0 flex-1`}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <Button intent="primary" size="small" data-approval-approve disabled={decide.isPending} onClick={() => decide.mutate({ requestId: pending.requestId, decision: 'approved' })}>
                  {t('approvals.approve')}
                </Button>
                <Button intent="secondary" size="small" data-approval-reject disabled={decide.isPending} onClick={() => decide.mutate({ requestId: pending.requestId, decision: 'rejected' })}>
                  {t('approvals.reject')}
                </Button>
              </div>
            ) : null}
            {pending.requestedBy === me ? (
              <div>
                <Button intent="ghost" size="small" data-approval-recall disabled={recall.isPending} onClick={() => recall.mutate(pending.requestId)}>
                  {t('approvals.recall')}
                </Button>
              </div>
            ) : null}
          </div>
        ) : processes.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <select data-approval-process aria-label={t('approvals.process')} className={INPUT} value={processId} onChange={(e) => setProcessId(e.target.value)}>
              <option value="">{t('approvals.chooseProcess')}</option>
              {processes.map((process) => (
                <option key={process.processId} value={process.processId}>{process.name}</option>
              ))}
            </select>
            <input
              data-approval-submit-comment
              aria-label={t('approvals.comment')}
              placeholder={t('approvals.comment')}
              className={`${INPUT} min-w-0 flex-1`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Button intent="secondary" size="small" data-approval-submit disabled={!processId || submit.isPending} onClick={() => submit.mutate()}>
              {t('approvals.submit')}
            </Button>
          </div>
        ) : null}
        {requests.filter((request) => request.status !== 'pending').slice(0, 3).map((request) => (
          <div key={request.requestId} className="border-t border-t-rule pt-2">
            <p className="text-meta text-ink-meta">{request.processName}</p>
            <ApprovalChainView chain={request} />
          </div>
        ))}
      </div>
    </Panel>
  );
}
